import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { LifeBuoy, Menu } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { QuickSwitcher } from "./QuickSwitcher";
import { Sidebar } from "./Sidebar";
import { ErrorBoundary } from "../ErrorBoundary";
import { Toaster } from "../ui/Toast";

export function Layout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const me = trpc.auth.me.useQuery(undefined, { retry: false });
  const endSupport = trpc.platform.support.end.useMutation({
    // Komplett neu laden: alle Abfragen müssen mit der eigenen Sitzung frisch geholt werden
    onSuccess: () => {
      window.location.href = "/plattform";
    },
  });

  // Menü beim Seitenwechsel automatisch schließen (sonst bleibt's auf dem Handy offen stehen).
  useEffect(() => setMobileNavOpen(false), [pathname]);

  return (
    <div className="flex h-full bg-bg-primary overflow-hidden">
      <Sidebar open={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {me.data?.impersonating && (
          <div className="flex items-center gap-3 px-4 py-2 bg-danger text-white text-sm flex-shrink-0">
            <LifeBuoy className="w-4 h-4 flex-shrink-0" />
            <span className="flex-1">
              Support-Zugang: Du bist als <b>{me.data.org.legalName}</b> angemeldet. Alle Aktionen werden protokolliert.
            </span>
            <button
              className="px-3 py-1 rounded bg-white/20 hover:bg-white/30 font-medium"
              disabled={endSupport.isPending}
              onClick={() => endSupport.mutate()}
            >
              Support beenden
            </button>
          </div>
        )}
        {/* Nur auf schmalen Bildschirmen — auf Desktop übernimmt die immer sichtbare Seitenleiste. */}
        <div className="md:hidden flex items-center gap-3 px-4 h-12 border-b border-border-subtle flex-shrink-0">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="text-text-secondary hover:text-text-primary p-1 -ml-1"
            aria-label="Menü öffnen"
          >
            <Menu className="w-5 h-5" />
          </button>
          <span className="text-sm font-semibold text-text-primary">
            Mavatech CRM
          </span>
        </div>
        <main className="flex-1 flex flex-col overflow-hidden min-w-0">
          <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>
        </main>
      </div>
      <Toaster />
      <QuickSwitcher />
    </div>
  );
}

export function PageHeader({
  title,
  actions,
}: {
  title: string;
  actions?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 md:px-6 py-3 md:py-4 border-b border-border-subtle flex-shrink-0 flex-wrap">
      <h1 className="text-lg md:text-xl font-semibold text-text-primary">
        {title}
      </h1>
      {actions}
    </div>
  );
}
